'use client'

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslations } from 'next-intl'

import { arrow, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react-dom'
import { Button, Icon } from '@graftcode/design-system'

import { useBreakpoint } from '@common/core/hooks'

import {
  PORTAL_TUTORIAL_POINTER_WIDTH,
  PORTAL_TUTORIAL_POPOVER_OFFSET,
  PORTAL_TUTORIAL_VIEWPORT_PADDING,
} from '../../const/portal-tutorial-layout.const'
import { PORTAL_TUTORIAL_POINTER_ROTATION } from '../../const/portal-tutorial-pointer-rotation.const'
import { PORTAL_TUTORIAL_POINTER_STATIC_SIDE } from '../../const/portal-tutorial-pointer-static-side.const'
import { PORTAL_TUTORIAL_STEPS, PORTAL_TUTORIAL_TOTAL_STEPS } from '../../const/portal-tutorial-steps.const'
import { usePortalTutorialPersistence } from '../../hooks/usePortalTutorialPersistence'
import { usePortalTutorialTargetElement } from '../../hooks/usePortalTutorialTargetElement'
import { usePortalTutorialTargetRect } from '../../hooks/usePortalTutorialTargetRect'
import { usePortalTutorialStore } from '../../stores/portal-tutorial.store'

import { PortalTutorialOverlay } from './PortalTutorialOverlay'

import styles from './PortalTutorial.module.scss'

type PortalTutorialProps = {
  autoStart?: boolean
}

export function PortalTutorial({ autoStart = false }: PortalTutorialProps) {
  const t = useTranslations('portalTutorial')
  const { isDesktop } = useBreakpoint()
  const { phase, currentStepIndex, openWelcome, startTour, nextStep, previousStep, dismiss, complete, reset } =
    usePortalTutorialStore()
  const { shouldAutoStart, markCompleted, markDismissed } = usePortalTutorialPersistence()

  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null)
  const [floatingElement, setFloatingElement] = useState<HTMLDivElement | null>(null)
  const currentStep = phase === 'active' ? PORTAL_TUTORIAL_STEPS[currentStepIndex] : null
  const targetRect = usePortalTutorialTargetRect(currentStep?.target ?? null, phase === 'active')
  const { element: targetElement, status: targetStatus } = usePortalTutorialTargetElement(
    currentStep?.target ?? null,
    phase === 'active'
  )

  const { floatingStyles, placement, middlewareData, isPositioned } = useFloating<HTMLElement>({
    placement: 'right',
    strategy: 'fixed',
    elements: { reference: targetElement, floating: floatingElement },
    middleware: [
      offset(PORTAL_TUTORIAL_POPOVER_OFFSET),
      flip({ padding: PORTAL_TUTORIAL_VIEWPORT_PADDING }),
      shift({ padding: PORTAL_TUTORIAL_VIEWPORT_PADDING }),
      arrow({ element: arrowElement, padding: PORTAL_TUTORIAL_VIEWPORT_PADDING }),
    ],
    whileElementsMounted: autoUpdate,
  })

  const isVisible = phase === 'welcome' || phase === 'active'
  const isLastStep = currentStepIndex === PORTAL_TUTORIAL_TOTAL_STEPS - 1

  useEffect(() => {
    if (!autoStart || !isDesktop || !shouldAutoStart) return
    openWelcome()
  }, [autoStart, isDesktop, openWelcome, shouldAutoStart])

  useEffect(() => {
    if (phase !== 'completed') return
    markCompleted()
    reset()
  }, [markCompleted, phase, reset])

  useEffect(() => {
    if (phase !== 'active' || targetStatus !== 'missing') return

    if (isLastStep) {
      complete()
      return
    }

    nextStep()
  }, [complete, isLastStep, nextStep, phase, targetStatus])

  if (!isDesktop || !isVisible || typeof document === 'undefined') {
    return null
  }

  const handleDismiss = () => {
    markDismissed()
    dismiss()
  }

  const handleFinish = () => {
    if (isLastStep) {
      complete()
      return
    }

    nextStep()
  }

  const pointerSide = placement.split('-')[0] as keyof typeof PORTAL_TUTORIAL_POINTER_STATIC_SIDE
  const arrowData = middlewareData.arrow

  return createPortal(
    <div className={styles.root} role="presentation">
      {phase === 'welcome' ? (
        <div className={styles.welcome}>
          <div
            className={styles.welcomeCard}
            role="dialog"
            aria-modal="true"
            aria-labelledby="portal-tutorial-welcome-title"
          >
            <div className={styles.welcomeHeader}>
              <h2 id="portal-tutorial-welcome-title" className={styles.welcomeTitle}>
                {t('welcome.title')}
              </h2>
            </div>
            <p className={styles.welcomeBody}>{t('welcome.description')}</p>
            <div className={styles.welcomeFooter}>
              <Button variant="outlined" size="m" text={t('welcome.maybeLater')} onClick={handleDismiss} />
              <Button variant="primary" size="m" text={t('welcome.letsGo')} onClick={startTour} />
            </div>
          </div>
        </div>
      ) : (
        <>
          <PortalTutorialOverlay targetRect={targetRect} showHighlight />
          {currentStep ? (
            <div
              ref={setFloatingElement}
              className={styles.popover}
              style={{
                ...floatingStyles,
                visibility: targetElement && isPositioned ? 'visible' : 'hidden',
              }}
              role="dialog"
              aria-modal="true"
            >
              <div
                ref={setArrowElement}
                className={styles.pointer}
                style={{
                  left: arrowData?.x != null ? `${arrowData.x}px` : undefined,
                  top: arrowData?.y != null ? `${arrowData.y}px` : undefined,
                  [PORTAL_TUTORIAL_POINTER_STATIC_SIDE[pointerSide]]: `-${PORTAL_TUTORIAL_POINTER_WIDTH}px`,
                  transform: `rotate(${PORTAL_TUTORIAL_POINTER_ROTATION[pointerSide]}deg)`,
                }}
                aria-hidden
              >
                <div className={styles.pointerShape} />
              </div>
              <div className={styles.popoverCard}>
                <div className={styles.popoverHeader}>
                  <h2 className={styles.popoverTitle}>{t(`steps.${currentStep.translationKey}.title`)}</h2>
                  <Button
                    className={styles.closeButton}
                    variant="ghost"
                    size="m"
                    iconOnly
                    icon={Icon.DismissRegular}
                    aria-label={t('actions.close')}
                    onClick={handleDismiss}
                  />
                </div>
                <p className={styles.popoverBody}>
                  {t.rich(`steps.${currentStep.translationKey}.description`, {
                    strong: (chunks) => <strong>{chunks}</strong>,
                  })}
                </p>
                <div className={styles.footer}>
                  <Button
                    variant="outlined"
                    size="m"
                    text={t('actions.back')}
                    disabled={currentStepIndex === 0}
                    onClick={previousStep}
                  />
                  <span className={styles.progress}>
                    {t('progress', {
                      current: currentStepIndex + 1,
                      total: PORTAL_TUTORIAL_TOTAL_STEPS,
                    })}
                  </span>
                  <Button
                    variant="primary"
                    size="m"
                    text={isLastStep ? t('actions.finish') : t('actions.next')}
                    onClick={handleFinish}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>,
    document.body
  )
}
